/**
 * safeStorage — defensive localStorage wrapper.
 *
 * Why: localStorage throws in Safari Private Mode and on quota-exceeded.
 * Without try/catch, the entire app crashes on read/write.
 *
 * This module only adds value for code that handles auth/critical state.
 * For non-critical preferences, plain localStorage is fine.
 */

const MEMORY_FALLBACK = new Map();

const isAvailable = (() => {
  try {
    const k = "__pp_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
})();

export const safeStorage = {
  get(key) {
    if (!isAvailable) return MEMORY_FALLBACK.get(key) ?? null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return MEMORY_FALLBACK.get(key) ?? null;
    }
  },

  set(key, value) {
    if (!isAvailable) {
      MEMORY_FALLBACK.set(key, value);
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // quota exceeded or storage disabled — fall back to memory for this session
      MEMORY_FALLBACK.set(key, value);
    }
  },

  remove(key) {
    if (!isAvailable) {
      MEMORY_FALLBACK.delete(key);
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      MEMORY_FALLBACK.delete(key);
    }
  },

  getJSON(key) {
    const raw = this.get(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  setJSON(key, value) {
    try {
      this.set(key, JSON.stringify(value));
    } catch {
      /* unserializable input — silently no-op */
    }
  },
};

/**
 * tokenStorage — convenience wrapper specifically for the JWT auth token.
 * Centralizes the key so future rotation is trivial and the token is
 * never read with raw `localStorage.getItem` (defense-in-depth for XSS).
 */
const TOKEN_KEY = "token";
const USER_KEY = "user";

export const tokenStorage = {
  get: () => safeStorage.get(TOKEN_KEY),
  set: (token) => safeStorage.set(TOKEN_KEY, token),
  clear: () => {
    safeStorage.remove(TOKEN_KEY);
    safeStorage.remove(USER_KEY);
  },
};

export const userStorage = {
  get: () => safeStorage.getJSON(USER_KEY),
  set: (user) => safeStorage.setJSON(USER_KEY, user),
};

export default safeStorage;
